import { useEffect, useRef, useState } from 'react'
import type { CardioLiveBlock, DspMetrics, EdaLiveBlock } from '@versports/ble-contracts'
import { controlDspMetrics } from '@versports/ble-contracts'
import { buildQuestWsUrl } from '../../api/physiology'

// El shape de `dsp_metrics` vive en `@versports/ble-contracts` porque el
// vr-app consume el mismo mensaje. Se re-exporta para no romper imports.
export type { CardioLiveBlock, DspMetrics, EdaLiveBlock }

interface LiveHrState {
  heartrate: number | null
  updatedAtMs: number | null
}

/** Últimas muestras de cada señal, para las sparklines de los tiles en vivo.
 * Sólo presentación: un número solo no deja ver si la señal está subiendo,
 * bajando o estable, que es justo lo que el jugador mira mientras juega. */
export interface LiveSeries {
  hr: number[]
  rLiveCardio: number[]
  scl: number[]
}

/** Cuántas muestras se retienen por señal. `dsp_metrics` llega cada 200 ms
 * (`DSP_PUSH_INTERVAL_S` en `vrs-sbc/rpi_bridge_ble/ws_client.py`), así que
 * son los últimos ~2 minutos: suficiente para ver la forma, barato de tener
 * en memoria y de redibujar. Si cambia la cadencia de la Pi, este número tiene
 * que acompañarla o la ventana visible se acorta sin que nadie lo pida. */
export const LIVE_SERIES_MAX = 600

export interface LivePhysiologyState {
  connected: boolean
  registered: boolean
  piOnline: boolean | null
  error: string | null
  metrics: DspMetrics | null
  hr: LiveHrState
  series: LiveSeries
}

const INITIAL_STATE: LivePhysiologyState = {
  connected: false,
  registered: false,
  piOnline: null,
  error: null,
  metrics: null,
  hr: { heartrate: null, updatedAtMs: null },
  series: { hr: [], rLiveCardio: [], scl: [] },
}

function pushSample(values: number[], sample: number | null | undefined): number[] {
  if (typeof sample !== 'number' || !Number.isFinite(sample)) return values
  const next = [...values, sample]
  return next.length > LIVE_SERIES_MAX ? next.slice(next.length - LIVE_SERIES_MAX) : next
}

function isKitScoped(data: Record<string, unknown>, kitId: string): boolean {
  // Mismo criterio que `ble-contracts::matchesKitScope` (vr-app): si el
  // mensaje trae `kit_id`, tiene que coincidir; si no lo trae, se acepta
  // (mensajes de sistema sin scope de kit).
  return !('kit_id' in data) || data.kit_id === kitId
}

/**
 * Consumidor read-only del WS de `edge_bridge` para el jugador (KNOW), sin
 * el resto del plumbing del vr-app (grabación, clock-sync, comandos de
 * control) — KNOW sólo mira, nunca controla el kit. Mismo protocolo
 * `register`/reconexión-con-backoff que
 * `vr-app/src/sensors/useEdgeSensorStream.ts`, pero KNOW es el web-app: la
 * cookie de sesión viaja sola en el handshake, así que el `register` no
 * lleva `token` (ver `web-app/src/api/client.ts`).
 */
export function useLivePhysiology(kit: { rpiId: string; kitId: string } | null): LivePhysiologyState {
  const [state, setState] = useState<LivePhysiologyState>(INITIAL_STATE)

  useEffect(() => {
    if (!kit) {
      setState(INITIAL_STATE)
      return
    }
    // Copia local no-nullable: TS no angosta `kit` (parámetro capturado)
    // dentro de las funciones anidadas de más abajo.
    const activeKit = kit

    let ws: WebSocket | null = null
    let closed = false
    let backoff = 1000
    let registered = false
    const clientInstanceId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `know-${Date.now()}`

    function handleMessage(event: MessageEvent): void {
      let data: Record<string, unknown>
      try {
        data = JSON.parse(event.data as string) as Record<string, unknown>
      } catch {
        return
      }

      if (data.type === 'registered') {
        registered = true
        setState((s) => ({ ...s, registered: true, error: null }))
        // `subscribe_ble_updates` es lo que prende el forwarder de streams en
        // la Pi (`ws_client.py::handle_subscribe_ble_updates`) — sin esto no
        // llegan ni los `ble_device_update` crudos NI `dsp_metrics` (la Pi
        // sólo manda `dsp_metrics` para kits con `is_kit_subscribed`, ver
        // `ws_client.py` ~L797).
        ws?.send(JSON.stringify({ action: 'subscribe_ble_updates', kit_id: activeKit.kitId }))
        return
      }

      if (data.type === 'superseded') {
        setState((s) => ({ ...s, error: 'Otra pestaña o dispositivo está leyendo estos sensores.' }))
        ws?.close()
        return
      }

      if (data.type === 'system_status') {
        setState((s) => ({ ...s, piOnline: Boolean(data.pi_online) }))
        return
      }

      if (data.type === 'error' && typeof data.message === 'string') {
        setState((s) => ({ ...s, error: data.message as string }))
        return
      }

      if (!isKitScoped(data, activeKit.kitId)) return

      if (data.type === 'dsp_metrics' && data.metrics && typeof data.metrics === 'object') {
        const metrics = controlDspMetrics(data.metrics as DspMetrics)
        setState((s) => ({
          ...s,
          metrics,
          error: null,
          series: {
            ...s.series,
            rLiveCardio: pushSample(s.series.rLiveCardio, metrics.cardio?.r_live_cardio),
            scl: pushSample(s.series.scl, metrics.eda?.scl?.value),
          },
        }))
        return
      }

      // Streams crudos — mismo parseo que
      // `vr-app/src/sensors/useEdgeSensorStream.ts` (`data.hr.heartrate`).
      if (data.type === 'ble_device_update') {
        const device = data.device as Record<string, unknown> | undefined
        const raw = device?.data as Record<string, unknown> | undefined
        const hrBlock = raw?.hr as Record<string, unknown> | undefined
        const heartrate = hrBlock && typeof hrBlock.heartrate === 'number' ? hrBlock.heartrate : null
        if (heartrate !== null) {
          setState((s) => ({
            ...s,
            hr: { heartrate, updatedAtMs: Date.now() },
            series: { ...s.series, hr: pushSample(s.series.hr, heartrate) },
          }))
        }
        return
      }
    }

    function connect(): void {
      if (closed) return
      try {
        const url = buildQuestWsUrl(activeKit.rpiId)
        ws = new WebSocket(url)
        registered = false

        ws.onopen = () => {
          backoff = 1000
          setState((s) => ({ ...s, connected: true, error: null }))
          ws?.send(
            JSON.stringify({
              type: 'register',
              kit_id: activeKit.kitId,
              // KNOW sólo mira — nunca es el dueño del kit. Sin esto, este
              // `register` se trataba como una reconexión del MISMO usuario
              // y desalojaba al vr-app real (le mandaba `superseded` y le
              // cortaba el WS), rompiendo la sesión en curso con solo abrir
              // esta pestaña. `observe: true` hace que el backend adjunte
              // este WS como observador de solo lectura sin tocar el claim
              // real (`_register_quest_ws`, edge_bridge.py) — y de paso
              // evita el código físico de verificación (vrs-docs/34 §2):
              // KNOW ni lo conoce, y no hace falta probarlo dos veces sobre
              // un kit que el propio usuario ya reclamó desde el vr-app.
              observe: true,
              client_instance_id: clientInstanceId,
              // Sin `token`: KNOW corre same-origin con la cookie de sesión,
              // que el backend ya valida ANTES de aceptar el WS (ver
              // `edge_bridge.py::_load_cookie_ws_user`) — no hace falta el
              // fallback de bearer token que sí necesita el vr-app.
            }),
          )
        }

        ws.onmessage = handleMessage

        ws.onclose = () => {
          setState((s) => ({ ...s, connected: false, registered: false }))
          ws = null
          registered = false
          if (!closed) {
            globalThis.setTimeout(connect, backoff)
            backoff = Math.min(backoff * 2, 30000)
          }
        }

        ws.onerror = () => ws?.close()
      } catch (err) {
        setState((s) => ({ ...s, error: String(err) }))
      }
    }

    connect()

    return () => {
      closed = true
      if (ws?.readyState === WebSocket.OPEN && registered) {
        ws.send(JSON.stringify({ action: 'unsubscribe_ble_updates', kit_id: activeKit.kitId }))
      }
      ws?.close()
      ws = null
      setState(INITIAL_STATE)
    }
  }, [kit?.rpiId, kit?.kitId])

  return state
}
